import React from 'react';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import { formatDate } from 'utils/date';
import { colors } from 'theme/colors';
import { Chips } from 'components/atoms/Chips';
import { StyledRouterLink } from 'components/atoms/RouterLink/styles';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { ERouteLinks } from 'models/route';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { ELicenceHistoryActions, ELicenceStatusIds } from 'pages/LegalEntitiesPage/helpers';
import { getDictionaryValue, getLicenceStatusChipVariant, getLicenceVariant } from './helpers';
import { StyledLink } from './styles';

interface IHistoryTableCardProps {
  data: any;
  weaponLicenceStatus: any[];
  t: (key: string) => string;
  handleOpenHistoryDetailsModal: (historyUniqueId: string) => void;
  showDetails: (description: string, externalReason: string, internalReason: string) => void;
}

const HistoryTableCard = ({
  data,
  weaponLicenceStatus,
  t,
  handleOpenHistoryDetailsModal,
  showDetails,
}: IHistoryTableCardProps) => {
  const { longDateFormat } = useGlobalProperty();
  const mappedValue = getDictionaryValue(data.statusId, weaponLicenceStatus || []);

  const isCreateAction =
    data.description && data.description.toUpperCase().includes(ELicenceHistoryActions.CREATE);

  return (
    <Grid
      container
      sx={{
        border: `1px solid ${colors.gray200}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        backgroundColor: colors.gray100,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        px: 2,
      }}
      columnSpacing={2}
    >
      <Grid size={{ xs: 12, sm: 6 }} sx={{ py: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.history.dateTime.column')}
        </Typography>
        <Typography variant="body2">
          {data.eventDate ? formatDate(data.eventDate, longDateFormat) : ''}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ py: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.history.licenceStatus.column')}
        </Typography>
        <Typography variant="body2">
          <Chips
            text={mappedValue}
            variant={getLicenceStatusChipVariant(mappedValue)}
            fullWidth
          />
        </Typography>
      </Grid>
      <Grid size={{ xs: 12 }} sx={{ py: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.history.description.column')}
        </Typography>
        <Typography
          variant="body2"
          component="div"
          sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}
        >
          {[
            ELicenceStatusIds.REVOKED,
            ELicenceStatusIds.SURRENDERED,
            ELicenceStatusIds.SUSPENDED,
            ELicenceStatusIds.ACTIVE,
          ].includes(data.statusId) ? (
            <StyledLink
              onClick={() =>
                showDetails(
                  getLicenceVariant(data.statusId),
                  data.externalReason,
                  data.internalReason,
                )
              }
            >
              {t(getLicenceVariant(data.statusId))}
            </StyledLink>
          ) : (
            t(data.description)
          )}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ py: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.history.userId.column')}
        </Typography>
        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
          {emptyFieldHolder(data.userId)}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ py: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.history.user.column')}
        </Typography>
        <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>
          {data.createdBy ? (
            <StyledRouterLink to={ERouteLinks.ViewUser.replace(':id', data.createdBy)}>
              {data.user}
            </StyledRouterLink>
          ) : (
            emptyFieldHolder(data.user)
          )}
        </Typography>
      </Grid>

      {!isCreateAction && (
        <Grid
          container
          size={{ xs: 12 }}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: {
              xs: `1px solid ${colors.primary100}`,
              lg: 'none',
            },
            py: 2,
          }}
        >
          <Grid size={{ xs: 12, sm: 6 }} sx={{ mb: { xs: 2, sm: 0 } }}>
            <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
              {t('legalEntity.licenceDetails.history.actions.column')}:{' '}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              id={`licence_history_card_viewDetails-button-${data.id}`}
              variant={EButtonVariants.outlined}
              tableButton
              label={t('licences.view.label')}
              onClick={() => handleOpenHistoryDetailsModal(data.id)}
              fullWidth
              size={EButtonSizes.small}
            />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export { HistoryTableCard };
